import React, { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useMagicReport } from '../../../magic-report/context/MagicReportContext';
import CoverPageTable from './CoverPageTable';
import { CoverPageDTO } from '../../../magic-report/magic-report.type';
import { useCompletionToggleEffect } from '../../../../../hooks/useCompletionToggleEffect';
import { calculateBudgetSummaryFromLineItems } from './budget-summary.util';

/**
 * "Amounts for Cover Letter" section, shown at the top of Payapp Review.
 * Reads/writes coverPage (and the drawSummary/budgetSummary/schedule fields it depends on)
 * directly off the shared MagicReportContext state.
 */
export const CoverLetterAmountsSection: React.FC = () => {
  const { reportState, setReportState, setIsDirty, saveReport } = useMagicReport();
  const { drawSummary, budgetSummary, coverPage } = reportState;

  // reportState.budgetSummary/draws[].budgetSummary only get their derived fields (%Complete,
  // retainage, etc.) refreshed once ReviewBudgetStep's line-items-changed effect has run and
  // written back into shared state — which happens after this section has already rendered.
  // Recompute the derived fields live from lineItems here too, so the Cover sheet never shows a
  // number that hasn't caught up with the latest line items yet.
  const liveBudgetSummary = useMemo(
    () => calculateBudgetSummaryFromLineItems(reportState.lineItems, budgetSummary) ?? budgetSummary,
    [reportState.lineItems, budgetSummary],
  );

  // The draws this report covers, each with its amounts recomputed the same way. Reports opened
  // before multi-draw (and non-magic reports) carry no `draws` list, so the single
  // drawSummary/budgetSummary trio becomes a one-draw list — that way every cover-letter row stays
  // tagged with the draw it came from instead of falling back to an untagged aggregate row.
  const liveDraws = useMemo(() => {
    const fromState = reportState.draws as any[] | undefined;
    const source = fromState?.length
      ? fromState
      : drawSummary
        ? [{ drawId: drawSummary.id, drawSummary, budgetSummary, lineItems: reportState.lineItems }]
        : [];
    return source.map((draw: any) => ({
      ...draw,
      budgetSummary: calculateBudgetSummaryFromLineItems(draw.lineItems, draw.budgetSummary) ?? draw.budgetSummary,
    }));
  }, [reportState.draws, drawSummary, budgetSummary, reportState.lineItems]);

  const isCoverPageCompleted = Boolean(coverPage?.isCompleted);
  const [coverPageExpanded, setCoverPageExpanded] = useState(() => !isCoverPageCompleted);

  const handleCoverPageChange = useCallback(
    (nextCoverPage: CoverPageDTO) => {
      setIsDirty(true);
      setReportState((prev: any) => ({ ...prev, coverPage: nextCoverPage }));
    },
    [setIsDirty, setReportState],
  );

  const handleToggleCoverPageCompleted = useCallback(() => {
    setIsDirty(true);
    setReportState((prev: any) => ({
      ...prev,
      coverPage: { ...(prev.coverPage || { budgetRows: [] }), isCompleted: !prev.coverPage?.isCompleted },
    }));
  }, [setIsDirty, setReportState]);

  const { isSavingCompletion } = useCompletionToggleEffect({
    isCompleted: isCoverPageCompleted,
    onToggle: nextCompleted => setCoverPageExpanded(!nextCompleted),
    saveReport,
  });

  return (
    <Box id='review-budget-cover-letter-amounts' sx={{ mb: 4 }}>
      <CoverPageTable
        coverPage={coverPage}
        budgetSummary={liveBudgetSummary}
        drawName={drawSummary?.name}
        draws={liveDraws}
        contractor={reportState.projectSummary?.contractor}
        onChange={handleCoverPageChange}
        isCompleted={isCoverPageCompleted}
        onToggleComplete={handleToggleCoverPageCompleted}
        isSavingComplete={isSavingCompletion}
        expanded={coverPageExpanded}
        onExpandedChange={setCoverPageExpanded}
      />
    </Box>
  );
};

export default CoverLetterAmountsSection;
